/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Backbone from 'backbone';
import _ from 'lodash';
import Ladda from 'ladda';
import Syphon from 'backbone.syphon';

import showErrorMessage from '@/util/showErrorMessage';

import Reset2FA from '../../services/Reset2FA';
import UpdateUserOTDSData from '../../services/UpdateUserOTDSGroups';
import SelectionCollectionView from '../SelectionCollectionView/SelectionCollectionView';

import tmplOTDSAccessView from './OTDSAccessView.hbs';
import './OTDSAccessView.css';

const OTDSAccessView = View.extend({
  template: tmplOTDSAccessView,

  ui: {
    placeholder: '.placeholder',
    formInputs: '.form-inputs',
    otdsUserID: '#otdsUserID',
    userInOTDS: '#userInOTDS',
    extGroupsSelectionCollectionView: '#ext-groups-selection-collectionview',
    intGroupsSelectionCollectionView: '#int-groups-selection-collectionview',
    extGroups: '.ext-groups',
    intGroups: '.int-groups',
    edipi: '.edipi',
    edipiText: '#edipi-text',
    edipiToggleBtn: '.btn-toggle-edipi',
    edipiInstructions: '#edipi-instructions',
    reset2faBtn: '.btn-reset-2fa',
    snowInstructions: '.snow-instructions'
  },

  regions: {
    extGroupsSelectionCollectionView: '@ui.extGroupsSelectionCollectionView',
    intGroupsSelectionCollectionView: '@ui.intGroupsSelectionCollectionView'
  },

  events: {
    'click @ui.reset2faBtn': 'resetTwoFactorAuthentication',
    'click @ui.edipiToggleBtn': 'toggleEdipiField'
  },

  templateContext() {
    return {
      otdsProfileExists: this.model.get('groups') !== undefined
    }
  },

  onRender() {
    const profile = this.model.get('profile');
    const groups  = this.model.get('groups');

    this.ui.extGroups.hide();
    this.ui.intGroups.hide();
    this.ui.edipi.hide();
    this.ui.reset2faBtn.hide();
    this.ui.snowInstructions.hide();
    this.isEdipiRendered = false;
    this.removedGroups = [];

    if (profile) {
      this.ui.formInputs.show();
      this.ui.placeholder.hide();
      this.updateProfile(profile, groups);
      if (groups) this.buildCollection(profile, groups);
    } else {
      this.ui.formInputs.hide();
      this.ui.placeholder.show();
    }
  },

  updateProfile(profile, groups) {
    const otdsProfileExists = groups !== undefined;
    if(otdsProfileExists) {
      $(this.ui.userInOTDS).text('Yes');
      $(this.ui.otdsUserID).text(profile.otdsUserId);
    } else {
      $(this.ui.userInOTDS).text('No');
      $(this.ui.otdsUserID).text('Unknown');
    }

    //show Service Now instructions for internal users
    if(profile.isExternal === '0') {
      this.ui.snowInstructions.show()
    }
  },

  buildCollection(data, groupCheckboxes) {
    if(!data || !groupCheckboxes) return;

    const groups = [];
    let validGroups = [];
    let region;

    if (data.isExternal && data.isExternal === '1') {
      $(this.ui.extGroups).show();
      //only show 2fa reset button if the user already has the EMS_EXT_EMAIL group
      //prevents accidentally turning on 2fa for CAC user
      if(_.includes(groupCheckboxes, 'EMS_EXT_EMAIL')) $(this.ui.reset2faBtn).show();

      validGroups = [{id: 1, userType: 'EMS_EXT_CAC', label: 'EMS_EXT_CAC', size: 'large'}, {id: 2, userType: 'EMS_EXT_EMAIL', label: 'EMS_EXT_EMAIL', size: 'large', disabled: true}];
      region = 'extGroupsSelectionCollectionView';
    } else {
      $(this.ui.intGroups).show();
      
      validGroups = [{ id: 1, userType: 'EMS All Users', label: 'EMS All Users', size: 'large', disabled: true }]
      region = 'intGroupsSelectionCollectionView';
    }

    _.each(validGroups, (grp) => {
      if(_.includes(groupCheckboxes, grp.userType)) groups.push(grp)
    })

    this.renderGroupCollection(validGroups, region);
    this.checkUsersGroupInputs(groups, data.isExternal);
    this.disableUsersGroupInputs(validGroups, data.isExternal);
  },

  renderGroupCollection(groups, region) {
    const collection = new Backbone.Collection(groups);
    const selectionCV = new SelectionCollectionView({ collection });
    this.showChildView(region, selectionCV);
  },

  checkUsersGroupInputs(groups, isExternal) {
    const groupIds = this.getGroupIds(groups);
    const collectionView = isExternal === '1' ? 'extGroupsSelectionCollectionView' : 'intGroupsSelectionCollectionView'
    this.getChildView(collectionView)?.setChecked(groupIds);
  },

  disableUsersGroupInputs(groups, isExternal) {
    const disabledGroups =  _.filter(groups, (grp) => { return grp.disabled });
    const groupIds = this.getGroupIds(disabledGroups);
    const collectionView = isExternal === '1' ? 'extGroupsSelectionCollectionView' : 'intGroupsSelectionCollectionView'
    this.getChildView(collectionView)?.setDisabled(groupIds);
  },

  getGroupIds(groups) {
    return _.map(groups, (grp) => (grp instanceof Backbone.Model ? grp.get('id') : grp.id));
  },

  getGroupNames(groups) {
    return _.map(groups, (grp) => (grp instanceof Backbone.Model ? grp.get('userType') : grp.userType));
  },

  async resetTwoFactorAuthentication(e) {
    e.preventDefault();

    const spinner = Ladda.create(this.ui.reset2faBtn[0]);
    spinner.start();

    try {
      await Reset2FA(this.model.get('profile').otdsUserId);
      spinner.stop();
    } catch(err) {
      showErrorMessage({
        errorCode: '500',
        errorTitle: 'RESET_2FA_ERROR',
        errorMessage: 'An error occurred while attempting to reset this user\'s two factor authentication. Please try again or contact an administrator if the issue persists.'
      });
    } finally {
      spinner.stop();
    }
  },

  async onSave() {
    const selectedGroups = await this.getCheckedGroups();
    if(selectedGroups) {
      const userData = await this.formatFormData(selectedGroups);

      try {
        await UpdateUserOTDSData(userData)
      } catch(err) {
        showErrorMessage({
          errorCode: 'userEditedError',
          errorTitle: 'User Edit Error',
          hideAfter: 10,
          errorMessage: 'Unable to update user\'s OTDS access. Please try again or submit a Service Now ticket for assistance.',
        });
      }
    }
  },

  async formatFormData(selectedGroups) {
    const currentGroups = this.model.get('groups') || [];

    const addedGroups = this.getAddedGroups(selectedGroups, currentGroups);
    const removedGroups = this.getRemovedGroups(selectedGroups, currentGroups);
    this.model.set('groups', selectedGroups);

    const edipi = Syphon.serialize(this).edipi;

    const userData = {
      otdsUserId: this.model.get('profile').otdsUserId,
      addedGroups,
      removedGroups,
      edipi
    }
    return userData;
  },

   getCheckedGroups() {
    const isExternal = this.model.get('profile').isExternal === '1';
    const childView = isExternal ? 'extGroupsSelectionCollectionView' : 'intGroupsSelectionCollectionView';
    if(this.getChildView(childView)) {
      const groups = this.getChildView(childView).getChecked();
      const selectedGroups = this.getGroupNames(groups);

      return selectedGroups;
    } else {
      return false;
    }
  },

  getAddedGroups(selectedGroups, currentGroups) {
    const addedGroups = [];
    _.each(selectedGroups, (selected) => {
      if(!currentGroups.includes(selected)) {
        addedGroups.push(selected);
      }
    })

    return addedGroups;
  },

  getRemovedGroups(selectedGroups, currentGroups) {
    const removedGroups = [];
    _.each(currentGroups, (current) => {
      if(!selectedGroups.includes(current)) {
        removedGroups.push(current);
      }
    })

    return removedGroups;
  },

  toggleEdipiField(e) {
    e?.preventDefault();
    const model = this.model.toJSON();

    if(Number.isInteger(parseInt(model.edipi))) {
      if (!this.ui.edipi.val()) Syphon.deserialize(this, model);
    } else {
      this.ui.edipiText.text('Unknown');
    } 
    if(this.isEdipiRendered) {
      this.ui.edipiInstructions.show();
      this.ui.edipi.hide();
    } else {
      this.ui.edipiInstructions.hide();
      this.ui.edipiText.show();
    }
    this.isEdipiRendered = !this.isEdipiRendered;
  }
});

export default OTDSAccessView;
